import { StudentAssignmentItem } from "../../../app/controllers/student-assignments-list/student-assignment-folder-item"

export interface StudentAssignmentRowViewModelProps {
  id: string
  type: "folder" | "assignment" | "inactive-header"
  name: string
  color?: string
  isWork: boolean
  isPromoted: boolean
  showStudentScores: boolean
  score: number | string | null
  totalPoints: number | null
  feedbackCount: number | null
  assignmentItem: StudentAssignmentItem | null
  subRows: StudentAssignmentRowViewModel[]
}

export class StudentAssignmentRowViewModel {
  constructor(private props: StudentAssignmentRowViewModelProps) {}

  get id(): string {
    return this.props.id
  }

  get type(): "folder" | "assignment" | "inactive-header" {
    return this.props.type
  }

  get name(): string {
    return this.props.name
  }

  get color(): string | undefined {
    return this.props.color
  }

  get isFolder(): boolean {
    return this.props.type === "folder"
  }

  get isAssignment(): boolean {
    return this.props.type === "assignment"
  }

  get isInactiveHeader(): boolean {
    return this.props.type === "inactive-header"
  }

  /**
   * Defines if a student has started work on this assignment already
   */
  get isWork(): boolean {
    return this.props.isWork
  }

  /**
   * Defines if this is an assignment that a student has yet to start
   */
  get isNewWork(): boolean {
    return this.isAssignment && !this.props.isWork
  }

  get isPromoted(): boolean {
    return this.props.isPromoted
  }

  get canClick(): boolean {
    return this.isAssignment
  }

  get feedbackCount(): number | null {
    if (
      !this.isWork ||
      !this.props.feedbackCount ||
      this.props.feedbackCount <= 0
    ) {
      return null
    }
    return this.props.feedbackCount
  }

  get pointsDisplay(): string | null {
    if (!this.isWork || !this.props.showStudentScores) {
      return null
    }
    if (typeof this.props.score !== "number") {
      return null
    }
    const potential = this.props.totalPoints ?? 0
    if (this.props.score === 0 && potential === 0) {
      return null
    }
    return `${this.props.score}/${potential} Pts`
  }

  get showNiceIndicator(): boolean {
    if (!this.isWork || !this.props.showStudentScores) {
      return false
    }
    const score = this.props.score
    const totalPoints = this.props.totalPoints
    return (
      typeof score === "number" &&
      typeof totalPoints === "number" &&
      totalPoints > 0 &&
      (score / totalPoints) * 100 >= 90
    )
  }

  get subRows(): StudentAssignmentRowViewModel[] {
    return this.props.subRows
  }

  get assignmentItem(): StudentAssignmentItem | null {
    return this.props.assignmentItem
  }

  static fromAssignmentItem(
    item: StudentAssignmentItem,
    color: string,
    idPrefix = "assignment",
  ): StudentAssignmentRowViewModel {
    return new StudentAssignmentRowViewModel({
      id: `${idPrefix}-${item.assignment.id}`,
      type: "assignment",
      name: item.name,
      color,
      isWork: item.isWork,
      isPromoted: item.isPromoted,
      showStudentScores: item.showStudentScores,
      score: item.studentTotalScore,
      totalPoints: item.totalPotentialPoints,
      feedbackCount: item.feedbackCount ?? null,
      assignmentItem: item,
      subRows: [],
    })
  }

  static fromFolder(
    folderId: string,
    title: string,
    color: string,
    activeAssignments: StudentAssignmentItem[],
    inactiveAssignments: StudentAssignmentItem[],
    rosterName?: string,
  ): StudentAssignmentRowViewModel {
    const assignmentRows = activeAssignments.map((item) =>
      StudentAssignmentRowViewModel.fromAssignmentItem(item, color),
    )

    if (inactiveAssignments.length > 0) {
      const inactiveRows = inactiveAssignments.map((item) =>
        StudentAssignmentRowViewModel.fromAssignmentItem(
          item,
          color,
          "inactive-assignment",
        ),
      )

      assignmentRows.push(
        new StudentAssignmentRowViewModel({
          id: `inactive-header-${folderId}`,
          type: "inactive-header",
          name: `Inactive Work${rosterName ? ` (${rosterName})` : ""}`,
          color,
          isWork: false,
          isPromoted: false,
          showStudentScores: false,
          score: null,
          totalPoints: null,
          feedbackCount: null,
          assignmentItem: null,
          subRows: inactiveRows,
        }),
      )
    }

    return new StudentAssignmentRowViewModel({
      id: `folder-${folderId}`,
      type: "folder",
      name: title,
      color,
      isWork: false,
      isPromoted: false,
      showStudentScores: false,
      score: null,
      totalPoints: null,
      feedbackCount: null,
      assignmentItem: null,
      subRows: assignmentRows,
    })
  }
}
